import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { Box, IconButton, Stack, Switch, Typography } from '@mui/material';

import { themeColors } from 'colors';

const ALL_PERMISSIONS_LABEL = 'All Permissions';
const AUTO_ENABLED_MODULE_LABEL_SUFFIX = 'auto-enabled';

const moduleHeaderRowSx = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: 2,
    rowGap: 1.25,
    py: 1,
} as const;

const moduleExpandIconSx = {
    color: themeColors.roleBuilder.roleName,
    transition: 'transform 0.2s',
} as const;

const moduleExpandIconCollapsedSx = {
    ...moduleExpandIconSx,
    transform: 'rotate(0deg)',
} as const;

const moduleExpandIconExpandedSx = {
    ...moduleExpandIconSx,
    transform: 'rotate(180deg)',
} as const;

const moduleTitleStackSx = {
    flex: '1 1 200px',
    minWidth: 0,
    maxWidth: '100%',
} as const;

const moduleHeaderActionsStackSx = {
    flexShrink: 0,
    justifyContent: 'flex-end',
} as const;

/** Matches permission description text size in PermissionRow (13px). */
const moduleAutoEnabledCountSx = {
    color: themeColors.roleBuilder.autoEnabledTagText,
    fontSize: '13px',
} as const;

export type PermissionModuleHeaderProps = {
    moduleKey: string;
    moduleTitle: string;
    selectedInModule: number;
    totalInModule: number;
    autoEnabledInModule: number;
    allInModuleSelected: boolean;
    isExpanded: boolean;
    readOnly: boolean;
    onToggleExpand: () => void;
    onToggleModuleAll: (checked: boolean) => void;
};

const PermissionModuleHeader = ({
    moduleKey,
    moduleTitle,
    selectedInModule,
    totalInModule,
    autoEnabledInModule,
    allInModuleSelected,
    isExpanded,
    readOnly,
    onToggleExpand,
    onToggleModuleAll,
}: PermissionModuleHeaderProps) => (
    <Box sx={moduleHeaderRowSx}>
        <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            columnGap={0.5}
            rowGap={0.25}
            sx={moduleTitleStackSx}
        >
            <Typography variant="body1" fontWeight={400} component="span">
                {moduleTitle}
            </Typography>
            <Typography variant="body1" fontWeight={400} component="span" color="text.primary">
                {` (${selectedInModule}/${totalInModule})`}
            </Typography>
            {autoEnabledInModule > 0 ? (
                <Typography
                    variant="body2"
                    component="span"
                    sx={moduleAutoEnabledCountSx}
                    data-testid={`access-center-module-auto-enabled-count-${moduleKey}`}
                >
                    {`${autoEnabledInModule} ${AUTO_ENABLED_MODULE_LABEL_SUFFIX}`}
                </Typography>
            ) : null}
        </Stack>
        <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={moduleHeaderActionsStackSx}
        >
            <Switch
                color="secondary"
                size="small"
                checked={allInModuleSelected}
                onChange={(_, v) => onToggleModuleAll(v)}
                disabled={readOnly}
                data-testid={`access-center-module-all-permissions-${moduleKey}`}
            />
            <Typography variant="body2" color="text.secondary" component="span">
                {ALL_PERMISSIONS_LABEL}
            </Typography>
            <IconButton
                size="small"
                onClick={onToggleExpand}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? 'Collapse module' : 'Expand module'}
            >
                <KeyboardArrowDownIcon sx={isExpanded ? moduleExpandIconExpandedSx : moduleExpandIconCollapsedSx} />
            </IconButton>
        </Stack>
    </Box>
);

export default PermissionModuleHeader;
